import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Modal,
  Typography,
  Paper,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Autocomplete,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ReceiptIcon from "@mui/icons-material/Receipt";
import RemoveIcon from "@mui/icons-material/Remove";
import ErrorIcon from "@mui/icons-material/Error";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  updateDoc,
} from "firebase/firestore";
import Swal from "sweetalert2";
import { StockItem } from "./StockManagement";
import moment from "moment";

export interface StockTransaction {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  type: "receipt" | "usage" | "adjustment" | "damage" | "return";
  quantity: number;
  reason: string;
  date: string;
  notes: string;
  createdAt: string;
}

type TransactionType = "receipt" | "usage" | "adjustment" | "damage" | "return";

const StockTaking: React.FC = () => {
  const [transactions, setTransactions] = useState<StockTransaction[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [formData, setFormData] = useState<Partial<StockTransaction>>({
    itemId: "",
    type: "usage",
    quantity: 0,
    reason: "",
    notes: "",
    date: moment().format("YYYY-MM-DD"),
  });
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    const user = auth.currentUser;
    if (!user) return;

    try {
      setLoading(true);

      // Fetch stock items
      const itemsCollection = collection(
        db,
        "stockItems",
        user.uid,
        "userStockItems",
      );
      const itemsSnapshot = await getDocs(query(itemsCollection));
      const items = itemsSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as StockItem,
      );
      setStockItems(items);

      // Fetch transactions
      const transactionsCollection = collection(
        db,
        "stockTransactions",
        user.uid,
        "userTransactions",
      );
      const transSnapshot = await getDocs(query(transactionsCollection));
      const trans = transSnapshot.docs.map(
        (doc) =>
          ({
            id: doc.id,
            ...doc.data(),
          }) as StockTransaction,
      );

      // Sort by date descending
      trans.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
      setTransactions(trans);
    } catch (error) {
      console.error("Error fetching data:", error);
      Swal.fire("Error", "Failed to load stock data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = () => {
    setFormData({
      itemId: "",
      type: "usage",
      quantity: 0,
      reason: "",
      notes: "",
      date: moment().format("YYYY-MM-DD"),
    });
    setSelectedItem(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleInputChange = (field: keyof StockTransaction, value: any) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSaveTransaction = async () => {
    const user = auth.currentUser;
    if (!user) return;

    // Validation
    if (
      !formData.itemId ||
      formData.quantity === undefined ||
      formData.quantity === 0
    ) {
      Swal.fire("Error", "Please select an item and enter a quantity", "error");
      return;
    }

    if (!selectedItem) {
      Swal.fire("Error", "Item not found", "error");
      return;
    }

    try {
      // Calculate new quantity
      let newQuantity = selectedItem.quantity;
      if (formData.type === "receipt") {
        newQuantity += formData.quantity;
      } else {
        newQuantity -= formData.quantity;
      }

      // Prevent negative stock
      if (newQuantity < 0) {
        Swal.fire(
          "Error",
          "This transaction would result in negative stock. Current quantity: " +
            selectedItem.quantity,
          "error",
        );
        return;
      }

      // Add transaction record
      const transactionsCollection = collection(
        db,
        "stockTransactions",
        user.uid,
        "userTransactions",
      );
      await addDoc(transactionsCollection, {
        itemId: formData.itemId,
        itemCode: selectedItem.itemCode,
        itemName: selectedItem.itemName,
        type: formData.type,
        quantity: formData.quantity,
        reason: formData.reason,
        notes: formData.notes,
        date: formData.date,
        createdAt: new Date().toISOString(),
      });

      // Update stock item quantity
      const itemDoc = doc(
        db,
        "stockItems",
        user.uid,
        "userStockItems",
        selectedItem.id,
      );
      await updateDoc(itemDoc, { quantity: newQuantity });

      Swal.fire("Success", "Stock transaction recorded", "success");
      handleCloseModal();
      fetchData();
    } catch (error) {
      console.error("Error saving transaction:", error);
      Swal.fire("Error", "Failed to save transaction", "error");
    }
  };

  const handleDeleteTransaction = async (id: string) => {
    const confirmResult = await Swal.fire({
      title: "Are you sure?",
      text: "Deleting this transaction will need to be reversed manually!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmResult.isConfirmed) return;

    const user = auth.currentUser;
    if (!user) return;

    try {
      const transDoc = doc(
        db,
        "stockTransactions",
        user.uid,
        "userTransactions",
        id,
      );
      await deleteDoc(transDoc);
      Swal.fire("Deleted!", "Transaction has been deleted.", "success");
      fetchData();
    } catch (error) {
      console.error("Error deleting transaction:", error);
      Swal.fire("Error", "Failed to delete transaction", "error");
    }
  };

  const getTransactionIcon = (type: TransactionType) => {
    switch (type) {
      case "receipt":
        return <ReceiptIcon color="success" />;
      case "usage":
        return <RemoveIcon color="error" />;
      case "damage":
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="bg-white shadow-md rounded p-4">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Stock Taking</Typography>
        {stockItems.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenModal}
          >
            Record Transaction
          </Button>
        )}
      </Box>

      {stockItems.length === 0 ? (
        <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
          No stock items found. Please create stock items first in Stock
          Management.
        </Typography>
      ) : transactions.length === 0 ? (
        <Typography color="textSecondary" align="center" sx={{ py: 4 }}>
          No transactions yet. Click "Record Transaction" to get started.
        </Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Item Code</TableCell>
                <TableCell>Item Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell align="right">Quantity</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id} hover>
                  <TableCell>
                    {moment(transaction.date).format("DD/MM/YYYY")}
                  </TableCell>
                  <TableCell>{transaction.itemCode}</TableCell>
                  <TableCell>{transaction.itemName}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getTransactionIcon(transaction.type as TransactionType)}
                      <span style={{ textTransform: "capitalize" }}>
                        {transaction.type}
                      </span>
                    </Box>
                  </TableCell>
                  <TableCell align="right">{transaction.quantity}</TableCell>
                  <TableCell>{transaction.reason}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {transaction.notes || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteTransaction(transaction.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Add Transaction Modal */}
      <Modal open={openModal} onClose={handleCloseModal}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            maxWidth: 500,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" sx={{ mb: 3 }}>
            Record Stock Transaction
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Autocomplete
              options={stockItems}
              getOptionLabel={(option) =>
                `${option.itemCode} - ${option.itemName}`
              }
              value={selectedItem}
              onChange={(_, newValue) => {
                setSelectedItem(newValue);
                setFormData({ ...formData, itemId: newValue?.id || "" });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Search Stock Item *"
                  placeholder="Type code or name..."
                  fullWidth
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">
                      <strong>{option.itemCode}</strong> - {option.itemName}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      Qty: {option.quantity} {option.unit} | Reorder:{" "}
                      {option.reorderLevel}
                    </Typography>
                  </Box>
                </Box>
              )}
              noOptionsText="No stock items found"
            />

            {selectedItem && (
              <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Current Quantity:</strong> {selectedItem.quantity}{" "}
                  {selectedItem.unit}
                </Typography>
                <Typography variant="body2">
                  <strong>Reorder Level:</strong> {selectedItem.reorderLevel}
                </Typography>
              </Box>
            )}

            <FormControl fullWidth>
              <InputLabel>Transaction Type *</InputLabel>
              <Select
                value={formData.type || "usage"}
                onChange={(e) =>
                  handleInputChange("type", e.target.value as TransactionType)
                }
                label="Transaction Type *"
              >
                <MenuItem value="receipt">Receipt (Stock In)</MenuItem>
                <MenuItem value="usage">Usage (Stock Out)</MenuItem>
                <MenuItem value="damage">Damage/Loss</MenuItem>
                <MenuItem value="return">Return</MenuItem>
                <MenuItem value="adjustment">Adjustment</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Quantity *"
              type="number"
              value={formData.quantity || 0}
              onChange={(e) =>
                handleInputChange("quantity", parseFloat(e.target.value))
              }
              fullWidth
              inputProps={{ step: "0.01", min: "0" }}
            />

            <TextField
              label="Date"
              type="date"
              value={formData.date || moment().format("YYYY-MM-DD")}
              onChange={(e) => handleInputChange("date", e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              label="Reason"
              value={formData.reason || ""}
              onChange={(e) => handleInputChange("reason", e.target.value)}
              fullWidth
              placeholder="e.g., Manufacturing, Sale, Damaged"
            />

            <TextField
              label="Notes"
              value={formData.notes || ""}
              onChange={(e) => handleInputChange("notes", e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Additional notes about this transaction"
            />
          </Box>

          <Box
            sx={{ display: "flex", gap: 2, mt: 4, justifyContent: "flex-end" }}
          >
            <Button variant="outlined" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveTransaction}
            >
              Record Transaction
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default StockTaking;
